import { useState, useEffect } from 'react';
import '../../css/home/HomeUpcoming.css';
import SectionHeader from './SectionHeader';
import { useLang } from '../../i18n';
import SportFilter from '../shared/SportFilter';
import MarketHeader from '../shared/MarketHeader';
import MatchCard from '../shared/MatchCard';

const API = process.env.REACT_APP_API_BASE_URL;
const CACHE_KEY  = 'home_upcoming_v1';
const CACHE_TTL  = 60 * 60 * 1000; // 1h

function formatDate(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) +
    ' · ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function mapFixture(m) {
  const mw   = m.odds?.match_winner || [];
  const home = mw.find(o => o.label === 'Home')?.odd;
  const draw = mw.find(o => o.label === 'Draw')?.odd;
  const away = mw.find(o => o.label === 'Away')?.odd;

  const totalMarkets = Object.values(m.odds || {}).reduce((s, a) => s + (Array.isArray(a) ? a.length : 0), 0);
  const extra = totalMarkets > 3 ? `+${totalMarkets - 3}` : undefined;

  return {
    home:   m.teams?.home?.name || '',
    away:   m.teams?.away?.name || '',
    live:   false,
    odds:   [
      { v: home != null ? String(home) : '-' },
      { v: draw != null ? String(draw) : '-' },
      { v: away != null ? String(away) : '-' },
    ],
    status: formatDate(m.fixture?.date),
    extra,
    href: `/match/football/${m.fixture?.id}`,
  };
}

function getCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) { localStorage.removeItem(CACHE_KEY); return null; }
    return data;
  } catch { return null; }
}

function setCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

// Ligues foot actives (avec leur saison réelle) pour pouvoir les sonder.
async function fetchFootballLeagues() {
  try {
    const res = await fetch(`${API}/api-bet/football/leagues.php`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    });
    const data = await res.json();
    if (!data.success) return [];
    return [...(data.top_leagues || []), ...(data.other_leagues || [])]
      .map(l => ({ id: l.league?.id, season: l.seasons?.[0]?.year }))
      .filter(l => l.id);
  } catch { return []; }
}

export default function HomeUpcoming() {
  const { t } = useLang();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cached = getCache();
    if (cached) { setMatches(cached); setLoading(false); return; }

    let cancelled = false;
    (async () => {
      try {
        const leagues = await fetchFootballLeagues();
        // Repli sur la Premier League (39) si leagues.php est injoignable.
        const candidates = leagues.length ? leagues.slice(0, 10) : [{ id: 39 }];
        let mapped = [];
        // On s'arrête à la 1re ligue qui a des matchs à venir.
        for (const lg of candidates) {
          if (cancelled) return;
          const url = `${API}/api-bet/football/matchs.php?league=${lg.id}${lg.season ? `&season=${lg.season}` : ''}`;
          let data;
          try { data = await (await fetch(url)).json(); } catch { continue; }
          if (data.success && data.fixtures?.length > 0) {
            mapped = data.fixtures.slice(0, 8).map(mapFixture);
            break;
          }
        }
        if (cancelled) return;
        if (mapped.length) { setCache(mapped); setMatches(mapped); }
      } catch {}
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loading && matches.length === 0) return null;

  return (
    <section>
      <SectionHeader title={t('home.upcomingMatches')} to="/football" />
      <SportFilter />
      <MarketHeader />
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '28px 0' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 0.8s linear infinite' }}>
            <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
            <circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,.1)" strokeWidth="2" />
            <path d="M12 3a9 9 0 018.66 6.5" stroke="#76e1ff" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
      ) : (
        <div className="home-upcoming-list">
          {matches.map((m, i) => <MatchCard key={i} match={m} />)}
        </div>
      )}
    </section>
  );
}
